import { BookingFormWrapper } from "PFApp/booking/components/booking_form";
import { PanelItem } from "PFApp/stores/side_panels_store";
import { useCallback } from "react";

import { BookingDetailSidePanel } from "../../parts/overview/detail_view/booking_detail/booking_detail_side_panel";
import { EngagementSidePanelWrapper } from "./detail_view/engagement_detail_side_panel";
import { MisalignmentsDetailSidePanel } from "./detail_view/misalignments_detail_side_panel/misalignments_detail_side_panel";
import { OverbookingsDetailSidePanel } from "./detail_view/overbookings_detail_side_panel";
import { ProfileDetailsSidePanel } from "./detail_view/profile_detail/profile_detail_side_panel";
import { ReassignSidePanel } from "./detail_view/reassign_side_panel/reassign_side_panel";
import { RoleDetailSidePanelWrapper } from "./detail_view/role_detail_side_panel";
import { BookingFormDetails, DetailsPanelData, DetailsPanelType } from "./details_panel.types";
import { useDetailsPanelsActions, usePrerequisiteChecker } from "./hooks";

type SidePanelComponent =
  | typeof BookingDetailSidePanel
  | typeof EngagementSidePanelWrapper
  | typeof ProfileDetailsSidePanel
  | typeof RoleDetailSidePanelWrapper
  | typeof OverbookingsDetailSidePanel
  | typeof MisalignmentsDetailSidePanel
  | typeof ReassignSidePanel
  | typeof BookingFormWrapper;

// Built lazily (not as a module-level const) so it's read after all modules have finished
// evaluating - some of these imports sit in circular dependency chains, and reading them
// eagerly at module-load time can throw "Cannot access ... before initialization".
const getSidePanel = (panelType: DetailsPanelType): SidePanelComponent => {
  const sidePanel: Record<DetailsPanelType, SidePanelComponent> = {
    booking: BookingDetailSidePanel,
    engagement: EngagementSidePanelWrapper,
    profile: ProfileDetailsSidePanel,
    role: RoleDetailSidePanelWrapper,
    overbookings: OverbookingsDetailSidePanel,
    misalignments: MisalignmentsDetailSidePanel,
    reassign: ReassignSidePanel,
    [DetailsPanelType.BookingForm]: BookingFormWrapper
  };

  return sidePanel[panelType];
};

type DetailsPanelItemProps = {
  panelData: PanelItem<DetailsPanelData>;
  isOnTop: boolean;
  activeBookingIndex?: number | undefined;
  setActiveBookingIndex?: (index: number | undefined) => void;
  bookingFormDetails?: BookingFormDetails;
  disableEntranceAnimation?: boolean;
};

export type DetailsSidePanelProps = {
  data: DetailsPanelData;
  show: boolean;
  isOnTop: boolean;
  onClose: () => void;
  isClosing?: boolean;
  zIndex?: number;
  disableEntranceAnimation?: boolean;
} & Omit<DetailsPanelItemProps, "panelData">;

export const DetailsPanelItem = ({
  panelData,
  isOnTop,
  activeBookingIndex,
  setActiveBookingIndex,
  bookingFormDetails,
  disableEntranceAnimation = false
}: DetailsPanelItemProps) => {
  const { data, isClosing, index } = panelData;
  const panelType = data.type;
  const checkIfSidePanelCanBeDisplayed = usePrerequisiteChecker({ data });
  const canDisplaySidePanel = checkIfSidePanelCanBeDisplayed({ type: panelType });
  const { closeDetailsPanel } = useDetailsPanelsActions();

  // Side panel is memoized - useCallback() to prevent unnecessary re-renders due to changed props
  const handleClose = useCallback(() => {
    closeDetailsPanel(panelType, data.id);
    data.onClose?.();
  }, [closeDetailsPanel, data, panelType]);

  const Panel = getSidePanel(panelType);

  if (!checkIfSidePanelCanBeDisplayed({ type: panelType })) {
    return null;
  }

  return (
    <Panel
      zIndex={index}
      isOnTop={isOnTop}
      show={canDisplaySidePanel}
      data={data}
      onClose={handleClose}
      isClosing={isClosing}
      activeBookingIndex={activeBookingIndex}
      setActiveBookingIndex={setActiveBookingIndex}
      bookingFormDetails={bookingFormDetails}
      disableEntranceAnimation={disableEntranceAnimation}
    />
  );
};
